import { forceRefreshToken, getCurrentToken } from "./token"

export class TermsSignaturePendingError extends Error {
  constructor() {
    super("terms signature pending, skipping token refresh")
    this.name = "TermsSignaturePendingError"
  }
}

export type TokenRetryOutcome<T> = { ok: true, value: T } | { ok: false, error: unknown }

interface RefreshPolicyOptions<T> {
  account: string
  userId: string
  /**
   * Read at retry time, never snapshotted: terms can go pending between the first attempt and the
   * refresh, and refreshing then logs the user out (see api/token.js waitWhilePendingTermsSignature).
   */
  getNeedSignTerms?: () => boolean
  isAuthFailure: (outcome: TokenRetryOutcome<T>) => boolean
  /** Surfacing hook for the blocked-refresh case; by default the first outcome is returned as-is. */
  onRefreshBlocked?: () => void
}

async function capture<T>(run: () => Promise<T>): Promise<TokenRetryOutcome<T>> {
  try {
    return { ok: true, value: await run() }
  } catch (error) {
    return { ok: false, error }
  }
}

function settle<T>(outcome: TokenRetryOutcome<T>): T {
  if (outcome.ok) return outcome.value
  throw outcome.error
}

/**
 * The single owner of the authenticated-retry policy: on an auth failure, refresh the token exactly
 * once and retry — unless terms are pending. Callers describe how to attempt and how to recognise an
 * auth failure; none of them decide when a refresh is allowed.
 */
async function runWithRefreshPolicy<T>(
  { account, userId, getNeedSignTerms, isAuthFailure, onRefreshBlocked }: RefreshPolicyOptions<T>,
  run: (refreshedToken?: string) => Promise<T>,
): Promise<T> {
  const first = await capture(() => run())
  if (!isAuthFailure(first)) return settle(first)

  if (getNeedSignTerms?.() === true) {
    onRefreshBlocked?.()
    return settle(first)
  }

  const refreshedToken = await forceRefreshToken(account, userId)
  return settle(await capture(() => run(refreshedToken)))
}

/** For attempts that need the token themselves (a raw `fetch` carrying `X-OmegaPoint-Code`). */
export async function withTokenRetry<T>(
  options: RefreshPolicyOptions<T> & { attempt: (token: string) => Promise<T> },
): Promise<T> {
  const { attempt, ...policy } = options
  const token = await getCurrentToken({ account: policy.account, userId: policy.userId })
  return runWithRefreshPolicy(policy, (refreshedToken) => attempt(refreshedToken ?? token))
}

/** For attempts that authenticate themselves (the `Creators.*` saga path), so no token is fetched here. */
export function retryOnAuthFailure<T>(
  options: RefreshPolicyOptions<T> & { attempt: () => Promise<T> },
): Promise<T> {
  const { attempt, ...policy } = options
  return runWithRefreshPolicy(policy, () => attempt())
}

/** Auth-failure predicate for attempts that resolve to a `Response`. */
export function isAuthFailureResponse(outcome: TokenRetryOutcome<Response>): boolean {
  return outcome.ok && (outcome.value.status === 401 || outcome.value.status === 403)
}

/** Auth-failure predicate for `Creators.*` saga attempts, which reject with a typed api error. */
export function isAuthFailureSagaError<T>(outcome: TokenRetryOutcome<T>): boolean {
  if (outcome.ok) return false
  const code = (outcome.error as { code?: unknown } | null)?.code
  return code === 401 || code === 403
}
